import { useEffect, useRef } from 'react';
import type { CustomFont } from '@proclaim/shared';

const STYLE_ID = 'proclaim-custom-fonts';

export function useFontLoader(customFonts: CustomFont[]) {
  const prevKey = useRef('');

  useEffect(() => {
    // Rebuild key from all file ids to detect any change
    const key = customFonts.flatMap((f) => f.files.map((ff) => ff.id)).join(',');
    if (key === prevKey.current) return;
    prevKey.current = key;

    let style = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
    if (!style) {
      style = document.createElement('style');
      style.id = STYLE_ID;
      document.head.appendChild(style);
    }

    // One @font-face per file — browser matches weight+style automatically.
    // Electron: always use local server path — R2 URLs are blocked by CORS from localhost.
    //   downloadMissingFontFiles ensures the file is on disk before this runs.
    // Web: prefer publicUrl (R2/CDN) so fonts uploaded from Electron load without
    //   needing the binary on VPS disk. Fall back to local server path for VPS uploads.
    const isElectron = !!window.electronAPI;
    style.textContent = customFonts
      .flatMap((f) =>
        f.files.map((ff) => {
          const src = isElectron
            ? `/uploads/fonts/${ff.filename}`
            : (ff.publicUrl ?? `/uploads/fonts/${ff.filename}`);
          return `
@font-face {
  font-family: '${f.cssFamily}';
  src: url('${src}');
  font-weight: ${ff.variable ? '100 900' : ff.weight};
  font-style: ${ff.style};
  font-display: swap;
}`;
        }),
      )
      .join('\n');
  }, [customFonts]);
}
